/* ==========================================================================
   Campaign Discount — cart logic
   - Reads the live cart (subtotal) from the theme's cart events / /cart.js
   - Toggles `.campaign-disc-unlocked` on <html> when subtotal >= threshold
     (site-wide: every campaign product card reacts via CSS, no per-card JS)
   - Updates the Campaign Cart Bar section when it is present on the page
   No client storage — the Shopify cart is the single source of truth.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.CampaignDiscount || {};
  var THRESHOLD = typeof cfg.threshold === 'number' ? cfg.threshold : 5000; // cents
  var RATE = typeof cfg.rate === 'number' ? cfg.rate : 0.20;                // fraction
  var RATE_PCT = typeof cfg.ratePct === 'number' ? cfg.ratePct : Math.round(RATE * 100);
  var MONEY_FORMAT = cfg.moneyFormat || '${{amount}}';
  var THRESHOLD_LABEL = cfg.thresholdLabel || '$50';

  var docEl = document.documentElement;

  /* --- Shopify-standard money formatter ---------------------------------- */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultOption(opt, def) { return typeof opt === 'undefined' ? def : opt; }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }

    var match = MONEY_FORMAT.match(placeholderRegex);
    switch (match && match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' '); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return MONEY_FORMAT.replace(placeholderRegex, value);
  }

  /* --- Cached bars (one or many on a page) -------------------------------
     Re-queried at the start of every render() so AJAX-replaced collection
     bars (filter/sort) and dynamically-injected bars stay covered. */
  var bars = [];
  function cacheBars() {
    bars = [];
    var nodes = document.querySelectorAll('[data-campaign-bar]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      bars.push({
        root: el,
        label: el.querySelector('[data-campaign-cart-label]'),
        total: el.querySelector('[data-campaign-total]'),
        fill: el.querySelector('[data-campaign-fill]'),
        msg: el.querySelector('[data-campaign-msg]'),
        savings: el.querySelector('[data-campaign-savings]'),
        subtotal: el.querySelector('[data-campaign-subtotal]'),
        discount: el.querySelector('[data-campaign-discount]'),
        youpay: el.querySelector('[data-campaign-youpay]'),
        paydisc: el.querySelector('[data-campaign-paydisc]'),
        savingsAmt: el.querySelector('[data-campaign-savings-amt]'),
        msgLocked: el.getAttribute('data-msg-locked') || '',
        msgUnlocked: el.getAttribute('data-msg-unlocked') || '',
        msgEmpty: el.getAttribute('data-msg-empty') || ''
      });
    }
  }

  function cartLabel(count) {
    var strings = window.themeStrings || {};
    if (!count) return strings.campaignCartCountZero || 'Cart';
    if (count === 1) return strings.campaignCartCountOne || 'Cart (1 item)';
    return strings.campaignCartCountOther
      ? strings.campaignCartCountOther.replace('__count__', count)
      : 'Cart (' + count + ' items)';
  }

  /* --- Render state from a cart object ----------------------------------- */
  function render(cart) {
    cart = cart || {};
    var subtotal = typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : 0;
    var count = typeof cart.item_count === 'number' ? cart.item_count : 0;
    var unlocked = subtotal >= THRESHOLD;

    // Site-wide switch — every campaign card reacts via CSS
    docEl.classList.toggle('campaign-disc-unlocked', unlocked);

    // Keep every nudge's "$X.XX more" amount live (product page + injected
    // quick-shop drawers). Queried fresh each render so dynamically-injected
    // quick-shop content is covered. The locked/unlocked copy swaps via CSS.
    var remaining = Math.max(0, THRESHOLD - subtotal);
    var remainingFmt = formatMoney(remaining);
    var nudgeAmounts = document.querySelectorAll('[data-campaign-nudge-remaining]');
    for (var n = 0; n < nudgeAmounts.length; n++) {
      nudgeAmounts[n].textContent = remainingFmt;
    }

    cacheBars();
    if (!bars.length) return;

    var pct = THRESHOLD > 0 ? Math.min(100, (subtotal / THRESHOLD) * 100) : 0;
    var discount = Math.round(subtotal * RATE);
    var youpay = subtotal - discount;

    for (var i = 0; i < bars.length; i++) {
      var b = bars[i];
      b.root.classList.toggle('is-unlocked', unlocked);
      if (b.label) b.label.textContent = cartLabel(count);
      if (b.total) b.total.textContent = formatMoney(subtotal);
      if (b.fill) b.fill.style.width = pct + '%';

      if (b.msg) {
        if (unlocked) {
          b.msg.textContent = b.msgUnlocked;
        } else if (subtotal > 0) {
          b.msg.textContent = b.msgLocked.replace('{remaining}', remainingFmt);
        } else {
          b.msg.textContent = b.msgEmpty;
        }
      }

      if (b.savings) {
        if (unlocked) b.savings.removeAttribute('hidden');
        else b.savings.setAttribute('hidden', '');
      }
      if (b.subtotal) b.subtotal.textContent = formatMoney(subtotal);
      if (b.discount) b.discount.textContent = '-' + formatMoney(discount);
      if (b.youpay) b.youpay.textContent = formatMoney(youpay);
      if (b.paydisc) b.paydisc.textContent = formatMoney(youpay);
      if (b.savingsAmt) b.savingsAmt.textContent = formatMoney(discount);
    }

    setupReveal();
  }

  /* --- Cart sources ------------------------------------------------------- */
  function fetchCart() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { if (data) render(data); })
      .catch(function () {});
  }

  /* --- Sticky scroll-reveal ----------------------------------------------
     Reveal the floating sticky bar only while the inline "anchor" bar is out
     of view. Re-attaches if the anchor element is swapped (AJAX re-render). */
  var revealObserver = null, observedAnchor = null;
  function setupReveal() {
    var reveal = document.querySelector('[data-campaign-reveal]');
    if (!reveal) return;
    // Re-home to <body> so position:fixed is viewport-relative even when an
    // ancestor (section wrapper, app, animation) sets transform/filter/contain,
    // which would otherwise trap the bar and keep it off-screen.
    if (reveal.parentNode !== document.body) {
      document.body.appendChild(reveal);
    }
    var anchor = document.querySelector('[data-campaign-anchor]');
    if (!anchor || !('IntersectionObserver' in window)) {
      reveal.classList.add('is-revealed'); // no anchor / no IO support → always show
      return;
    }
    if (anchor === observedAnchor && revealObserver) return; // already wired
    if (revealObserver) revealObserver.disconnect();
    observedAnchor = anchor;
    revealObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        reveal.classList.toggle('is-revealed', !entries[i].isIntersecting);
      }
    }, { threshold: 0 });
    revealObserver.observe(anchor);
  }

  function init() {
    // The theme dispatches cart:updated with detail.cart on every change
    document.addEventListener('cart:updated', function (e) {
      if (e && e.detail && e.detail.cart) render(e.detail.cart);
      else fetchCart();
    });
    document.addEventListener('cart:cleared', function () { render({ items_subtotal_price: 0, item_count: 0 }); });
    // Initial read (covers first paint + bfcache restore)
    fetchCart();
    setupReveal();
  }

  // pageshow fires on first load AND on bfcache back-button restore
  window.addEventListener('pageshow', function () {
    fetchCart();
    setupReveal();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* --- Swatch hover tooltip (ported, globally guarded) -------------------
     Campaign cards reuse the theme's swatch markup. On pages where the old
     product-grid-item no longer renders, its per-card tooltip script is gone,
     so we provide one shared, idempotent copy here. The guard flag is the same
     one product-grid-item uses, so the two never double-bind. */
  (function () {
    if (window.swatchTooltipInitialized) return;
    window.swatchTooltipInitialized = true;

    function showTooltip(e) {
      if (!(e.target instanceof Element)) return;
      var wrapper = e.target.closest('[data-swatch-tooltip]');
      if (!wrapper) return;
      var swatchesEl = wrapper.closest('.productitem--swatches');
      if (!swatchesEl) return;
      var tooltip = swatchesEl.querySelector('[data-swatch-hover-tooltip]');
      if (!tooltip) return;
      var text = wrapper.getAttribute('data-swatch-tooltip');
      if (!text) return;

      tooltip.textContent = text;
      tooltip.style.left = '0px';
      tooltip.style.transform = 'none';
      tooltip.classList.add('visible');

      var wrapperRect = wrapper.getBoundingClientRect();
      var parentRect = swatchesEl.getBoundingClientRect();
      var tooltipWidth = tooltip.offsetWidth;
      var containerWidth = swatchesEl.offsetWidth;
      var swatchCenter = wrapperRect.left - parentRect.left + wrapperRect.width / 2;
      var idealLeft = swatchCenter - tooltipWidth / 2;
      var maxLeft = Math.max(0, containerWidth - tooltipWidth);
      var clampedLeft = Math.max(0, Math.min(idealLeft, maxLeft));

      tooltip.style.left = clampedLeft + 'px';
      tooltip.style.transform = 'none';
      tooltip.style.setProperty('--arrow-left', (swatchCenter - clampedLeft) + 'px');
    }

    function hideTooltip(e) {
      if (!(e.target instanceof Element)) return;
      var wrapper = e.target.closest('[data-swatch-tooltip]');
      if (!wrapper) return;
      var swatchesEl = wrapper.closest('.productitem--swatches');
      if (!swatchesEl) return;
      var tooltip = swatchesEl.querySelector('[data-swatch-hover-tooltip]');
      if (tooltip) tooltip.classList.remove('visible');
    }

    document.addEventListener('mouseenter', showTooltip, true);
    document.addEventListener('mouseleave', hideTooltip, true);
    document.addEventListener('touchstart', showTooltip, true);
    document.addEventListener('touchend', function () {
      setTimeout(function () {
        document.querySelectorAll('[data-swatch-hover-tooltip].visible').forEach(function (t) {
          t.classList.remove('visible');
        });
      }, 1500);
    }, true);
  })();
})();
